import {
  foreignKey,
  index,
  jsonb,
  pgTable,
  text,
  uuid,
  integer,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { id, temporalInstant, sortIndex } from "../../utils/sql";
import { relations } from "drizzle-orm";
import { mealPlanMealWorkflowExecution } from "../../nutrition/meal-plan-agent/meal-plan-agent.sql";
import { mealPlanningWorkflowExecution } from "../../nutrition/meal-planning-agent/meal-planning-agent.sql";
import type { ErrorInfo } from "../common";

/**
 * Workflow-execution persistence schema.
 *
 * Invariants:
 * - Execution rows represent workflow-level status/context and optional error.
 * - Step rows are ordered per execution by `sortIndex` and support parent-child
 *   fan-out/fan-in relationships.
 *
 * @see `packages/core/docs/database.md`
 */
export type WorkflowExecutionStatus =
  | "pending"
  | "running"
  | "completed"
  | "completed_with_errors"
  | "error"
  | "canceled";

export type WorkflowExecutionStepStatus =
  | "pending"
  | "running"
  | "completed"
  | "error"
  | "canceled";

export const workflowExecution = pgTable(
  "workflow_execution",
  {
    ...id,
    timeCreated: temporalInstant("time_created").notNull(),
    workflowType: text("workflow_type").notNull(),
    workflowVersion: integer("workflow_version").notNull(),
    status: text("status").$type<WorkflowExecutionStatus>().notNull(),
    context: jsonb("context").notNull(),
    error: jsonb("error").$type<ErrorInfo>(),
    metadata: jsonb("metadata"),
  },
  (t) => [index("workflow_execution_type_status").on(t.workflowType, t.status)],
);

export const workflowExecutionRelations = relations(workflowExecution, ({ many }) => ({
  steps: many(workflowExecutionStep),
  mealWorkflowExecutions: many(mealPlanMealWorkflowExecution),
  mealPlanningWorkflowExecutions: many(mealPlanningWorkflowExecution),
}));

export const workflowExecutionStep = pgTable(
  "workflow_execution_step",
  {
    ...id,
    ...sortIndex,
    timeCreated: temporalInstant("time_created").notNull(),
    executionId: uuid("execution_id")
      .references(() => workflowExecution.id)
      .notNull(),
    stepName: text("step_name").notNull(),
    status: text("status").$type<WorkflowExecutionStepStatus>().notNull(),
    input: jsonb("input").notNull(),
    output: jsonb("output"),
    error: jsonb("error").$type<ErrorInfo>(),
    metadata: jsonb("metadata"),
    retryCount: integer("retry_count").notNull(),
    maxRetries: integer("max_retries").notNull(),
    parentStepId: uuid("parent_step_id"),
    timeStarted: temporalInstant("time_started"),
    timeEnded: temporalInstant("time_ended"),
  },
  (t) => [
    foreignKey({
      name: "workflow_execution_step_parent_fk",
      columns: [t.parentStepId],
      foreignColumns: [t.id],
    }),
    uniqueIndex("workflow_execution_step_execution_sort_index_unique").on(
      t.executionId,
      t.sortIndex,
    ),
    index("workflow_execution_step_execution_status").on(t.executionId, t.status),
    index("workflow_execution_step_parent").on(t.parentStepId),
  ],
);

export const workflowExecutionStepRelations = relations(workflowExecutionStep, ({ one, many }) => ({
  execution: one(workflowExecution, {
    fields: [workflowExecutionStep.executionId],
    references: [workflowExecution.id],
  }),
  parentStep: one(workflowExecutionStep, {
    fields: [workflowExecutionStep.parentStepId],
    references: [workflowExecutionStep.id],
    relationName: "parentChild",
  }),
  childSteps: many(workflowExecutionStep, { relationName: "parentChild" }),
}));
